import React, {ChangeEvent, useCallback, useContext, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {isEmail} from 'validator';

import {
    Button,
    Checkbox,
    CheckboxProps,
    Group,
    IconHelp,
    RadioButton,
    RadioButtons,
    RadioButtonsType,
    Select,
    SelectInterface,
    TextBox,
    TextBoxProps,
    useToast,
} from '@veneer/core';

import {
    Country,
    ResponseError,
    TenantDetail,
    TenantDetailError,
    TenantDetailErrorFromJSON,
    TenantPermission,
} from '@br/idm-api';
import {useIntlFormatter, useTenantContext, useTenantUser} from '@br/idm-components';
import {A, ButtonLink, FormError, useWolfTheme} from '@br/mg-components';

import {GlobalTranslationsContext} from '../../i18n/global';
import {toLowerCaseAscii} from '../../util/ascii';
import {isGuid} from '../../util/guid';
import {AddPartner} from '../AddPartner';
import {RemovePartner} from '../RemovePartner';

import useStyle from './styles';

const Settings = ({cancelUrl}: {cancelUrl: string}): JSX.Element => {
    const {t} = useTranslation(['settings']);

    const {addToast} = useToast();

    const {hasPermission} = useTenantUser();
    const {tenant, updateTenant} = useTenantContext();
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);

    const [isSaving, setIsSaving] = useState(false);

    type SettingsFormKeys =
        | 'name'
        | 'require2fa'
        | 'partnerId'
        | 'azureTenantId'
        | 'isBusiness'
        | 'companyName'
        | 'addressLine1'
        | 'addressLine2'
        | 'addressCity'
        | 'addressCounty'
        | 'addressPostCode'
        | 'addressCountry'
        | 'contactName'
        | 'contactEmail';
    type FormState = Pick<TenantDetail, SettingsFormKeys | 'partnerName'>;

    const [formState, setFormState] = useState<FormState>({...tenant});
    const [formError, setFormError] = useState<TenantDetailError>({});

    const updateFormState = useCallback(
        (key: SettingsFormKeys, value) => {
            // Clear error when typing
            if (formError[key] !== undefined) {
                setFormError(formError => ({...formError, [key]: undefined}));
            }
            setFormState(formState => ({...formState, [key]: value}));
        },
        [formError]
    );

    const updateIsBusinessFormState = useCallback((isBusiness: boolean) => {
        // This is different from updateFormState since we clear fields
        setFormError(formError => ({...formError, isBusiness: undefined}));
        setFormState(formState => ({
            ...formState,
            isBusiness,
            ...(formState.isBusiness !== null && {
                contactName: '',
                contactEmail: '',
            }),
            ...(!isBusiness && {
                companyName: '',
                addressLine1: '',
                addressLine2: '',
                addressCity: '',
                addressCounty: '',
                addressPostCode: '',
                addressCountry: undefined,
            }),
        }));
    }, []);

    const saveSettings = useCallback(
        (event: React.SyntheticEvent) => {
            event.preventDefault();

            const validateAzureTenantId = (formState: FormState): boolean => {
                const {azureTenantId} = formState;
                if (!azureTenantId) {
                    return true;
                }
                if (!isGuid(azureTenantId)) {
                    return false;
                }
                const msConsumerTenantId = '9188040d-6c67-4c5b-b112-36a304b66dad';
                return toLowerCaseAscii(azureTenantId) !== msConsumerTenantId;
            };

            let success: string | null = null;
            let newFormError: TenantDetailError = {};
            setIsSaving(false);

            const addErrorToast = () =>
                addToast({
                    id: 'settingsFailure',
                    type: 'negative',
                    text: t('settings:error', 'There was a problem saving your changes. Please try again.'),
                });

            if (!validateAzureTenantId(formState)) {
                setFormError({
                    azureTenantId: [
                        t('settings:invalidAzureTenantId', 'Microsoft Entra tenant ID must be a valid GUID or empty.'),
                    ],
                });
                addErrorToast();
                return;
            }

            setIsSaving(true);

            updateTenant(formState)
                .then(() => {
                    success = t('settings:success', 'Your changes have been saved.');
                })
                .catch(async (error: ResponseError) => {
                    if (error.response?.status === 400) {
                        // Django validates emails differently so want to see if an
                        // error exists and if so display helper text.
                        newFormError = TenantDetailErrorFromJSON(await error.response.json());
                    }
                })
                .finally(() => {
                    setIsSaving(false);
                    setFormError(newFormError);
                    if (success) {
                        addToast({
                            id: 'settingsSuccess',
                            type: 'positive',
                            text: success,
                        });
                    } else {
                        addErrorToast();
                    }
                });
        },
        [addToast, formState, t, updateTenant]
    );

    const handleNameChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('name' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleBusinessOrPersonalChange = useCallback<Required<RadioButtonsType>['onChange']>(
        (_: ChangeEvent, value: string) => updateIsBusinessFormState(value === 'business'),
        [updateIsBusinessFormState]
    );
    const handleCompanyNameChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('companyName' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleAddressLine1Change = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('addressLine1' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleAddressLine2Change = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('addressLine2' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleAddressCityChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('addressCity' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleAddressCountyChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('addressCounty' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleAddressPostCodeChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('addressPostCode' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleAddressCountryChange: Required<SelectInterface<Country>>['onChange'] = useCallback(
        value => updateFormState('addressCountry' as SettingsFormKeys, value.value),
        [updateFormState]
    );
    const handleContactNameChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('contactName' as SettingsFormKeys, value),
        [updateFormState]
    );
    const handleContactEmailChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('contactEmail' as SettingsFormKeys, value),
        [updateFormState]
    );

    const handleRemovePartner = useCallback(
        () => setFormState(formState => ({...formState, partnerId: null, partnerName: null})),
        []
    );
    const handleAddPartner = useCallback(
        ({id, name}: {id: string; name: string}) =>
            setFormState(formState => ({...formState, partnerId: id, partnerName: name})),
        []
    );
    const handleAzureTenantIdChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => updateFormState('azureTenantId' as SettingsFormKeys, value.length === 0 ? null : value),
        [updateFormState]
    );
    const handleTwoFactorChange = useCallback<Required<CheckboxProps>['onChange']>(
        e => setFormState(formState => ({...formState, require2fa: e.target.checked})),
        []
    );

    const theme = useWolfTheme();

    const {filterCountrySelection} = useIntlFormatter();
    const [countryFilter, setCountryFilter] = useState('');

    const handleCountrySearch = useCallback<Required<SelectInterface>['onSearch']>(query => {
        setCountryFilter(query || '');
    }, []);

    const helperText = t('settings:requiredHelperText', 'This field is required.');

    // Cannot put error on radio buttons see https://github.azc.ext.hp.com/veneer/veneer/issues/5051
    const radioButtonError = formError.isBusiness?.[0] !== undefined || formState.isBusiness === null;
    const style = useStyle(radioButtonError);

    return (
        <>
            <form
                onSubmit={saveSettings}
                data-test-id="tenant-settings-form"
            >
                <fieldset className="mb-6">
                    <legend className="subtitle-large">{t('settings:general', 'General')}</legend>
                    <TextBox
                        className="my-3"
                        value={formState.name}
                        id="tenant_name"
                        label={t('settings:organisationName', 'Organization name')}
                        onChange={handleNameChange}
                        error={formError.name?.[0] !== undefined || formState.name.length === 0}
                        helperText={formError.name?.[0] || (formState.name.length === 0 ? helperText : undefined)}
                        helperTextState="error"
                        maxLength={100}
                        required
                        separateLabel
                    />

                    <p
                        style={{color: theme.color.gray6}}
                        className="mb-2 d-block"
                    >
                        {t('settings:personalOrBusinessUse', 'Personal or business use') + ' *'}
                    </p>

                    <RadioButtons
                        data-testid="personal-or-business-radio-buttons"
                        className={`${style.radioButton} my-3`}
                        id="tenant_business_or_personal_use"
                        name="businessOrPersonalUse"
                        onChange={handleBusinessOrPersonalChange}
                        defaultValue={
                            formState.isBusiness === undefined || formState.isBusiness === null
                                ? undefined
                                : formState.isBusiness
                                  ? 'business'
                                  : 'personal'
                        }
                        alignment="horizontal"
                    >
                        <RadioButton
                            id="tenant_personal"
                            value="personal"
                            label={t('settings:personalUse', 'Personal')}
                        />
                        <RadioButton
                            id="tenant_business"
                            value="business"
                            label={t('settings:businessUse', 'Business')}
                        />
                        <FormError error={radioButtonError ? formError.isBusiness?.[0] || helperText : undefined} />
                    </RadioButtons>

                    {formState.isBusiness === true && (
                        <>
                            <TextBox
                                className="my-3"
                                value={formState.companyName}
                                id="company_name"
                                label={t('settings:companyName', 'Legal entity name')}
                                onChange={handleCompanyNameChange}
                                error={
                                    formError.companyName?.[0] !== undefined ||
                                    formState.companyName === undefined ||
                                    formState.companyName.length === 0
                                }
                                helperText={
                                    formError.companyName?.[0] ||
                                    (formState.companyName === undefined || formState.companyName.length === 0
                                        ? helperText
                                        : undefined)
                                }
                                helperTextState="error"
                                required
                                separateLabel
                            />
                            <Group>
                                <TextBox
                                    className="my-3"
                                    value={formState.addressLine1}
                                    id="address_line_1"
                                    label={t('settings:addressLine1', 'Address line 1')}
                                    onChange={handleAddressLine1Change}
                                    error={
                                        formError.addressLine1?.[0] !== undefined ||
                                        formState.addressLine1 === undefined ||
                                        formState.addressLine1.length === 0
                                    }
                                    helperText={
                                        formError.addressLine1?.[0] ||
                                        (formState.addressLine1 === undefined || formState.addressLine1.length === 0
                                            ? helperText
                                            : undefined)
                                    }
                                    helperTextState="error"
                                    required
                                    separateLabel
                                />
                                <TextBox
                                    className="my-3"
                                    value={formState.addressLine2}
                                    id="address_line_2"
                                    label={t('settings:addressLine2', 'Address line 2')}
                                    onChange={handleAddressLine2Change}
                                    error={formError.addressLine2?.[0] !== undefined}
                                    helperText={formError.addressLine2?.[0]}
                                    helperTextState="error"
                                    separateLabel
                                />
                            </Group>
                            <Group>
                                <TextBox
                                    className="my-3"
                                    value={formState.addressCity}
                                    id="address_city"
                                    label={t('settings:addressCity', 'City/town')}
                                    onChange={handleAddressCityChange}
                                    error={
                                        formError.addressCity?.[0] !== undefined ||
                                        formState.addressCity === undefined ||
                                        formState.addressCity.length === 0
                                    }
                                    helperText={
                                        formError.addressCity?.[0] ||
                                        (formState.addressCity === undefined || formState.addressCity.length === 0
                                            ? helperText
                                            : undefined)
                                    }
                                    helperTextState="error"
                                    required
                                    separateLabel
                                />
                                <TextBox
                                    className="my-3"
                                    value={formState.addressCounty}
                                    id="address_county"
                                    label={t('settings:addressCounty', 'County/state')}
                                    onChange={handleAddressCountyChange}
                                    error={formError.addressCounty?.[0] !== undefined}
                                    helperText={formError.addressCounty?.[0]}
                                    helperTextState="error"
                                    separateLabel
                                />
                            </Group>
                            <Group>
                                <TextBox
                                    className="my-3"
                                    value={formState.addressPostCode}
                                    id="address_post_code"
                                    label={t('settings:addressPostCode', 'Postal/ZIP code')}
                                    onChange={handleAddressPostCodeChange}
                                    error={
                                        formError.addressPostCode?.[0] !== undefined ||
                                        formState.addressPostCode === undefined ||
                                        formState.addressPostCode.length === 0
                                    }
                                    helperText={
                                        formError.addressPostCode?.[0] ||
                                        (formState.addressPostCode === undefined ||
                                        formState.addressPostCode.length === 0
                                            ? helperText
                                            : undefined)
                                    }
                                    helperTextState="error"
                                    required
                                    separateLabel
                                />
                                <Select
                                    className="my-3"
                                    data-testid="tenant-address-country-dropdown"
                                    options={filterCountrySelection(countryFilter)}
                                    label={t('settings:addressCountry', 'Country')}
                                    clearIcon={false}
                                    value={formState.addressCountry ? [formState.addressCountry] : []}
                                    onChange={handleAddressCountryChange}
                                    showSearch
                                    onSearch={handleCountrySearch}
                                    error={
                                        formError.addressCountry?.[0] !== undefined ||
                                        formState.addressCountry === undefined ||
                                        formState.addressCountry === null
                                    }
                                    helperText={
                                        formError.addressCountry?.[0] ||
                                        (formState.addressCountry === undefined || formState.addressCountry === null
                                            ? helperText
                                            : undefined)
                                    }
                                    i18n={veneerTranslations.select}
                                    required
                                    separateLabel
                                />
                            </Group>
                        </>
                    )}
                    {formState.isBusiness !== null && (
                        <>
                            <Group>
                                <TextBox
                                    className="my-3"
                                    value={formState.contactName}
                                    id="contact_name"
                                    label={t('settings:contactName', 'Contact name')}
                                    onChange={handleContactNameChange}
                                    error={formError.contactName?.[0] !== undefined}
                                    helperText={formError.contactName?.[0]}
                                    separateLabel
                                />
                                <TextBox
                                    className="my-3"
                                    value={formState.contactEmail}
                                    id="contact_email"
                                    label={t('settings:contactEmail', 'Contact email')}
                                    onChange={handleContactEmailChange}
                                    error={
                                        formError.contactEmail?.[0] !== undefined ||
                                        !isEmail(formState.contactEmail) ||
                                        formState.contactEmail.length === 0
                                    }
                                    helperText={
                                        formError.contactEmail?.[0] ||
                                        (formState.contactEmail.length !== 0
                                            ? !isEmail(formState.contactEmail)
                                                ? t(
                                                      'settings:invalidEmailError',
                                                      'You must provide a valid contact email.'
                                                  )
                                                : undefined
                                            : helperText)
                                    }
                                    helperTextState="error"
                                    separateLabel
                                    required
                                />
                            </Group>
                        </>
                    )}
                    <div className="my-3 d-flex align-items-end">
                        <div className="flex-grow-1">
                            <p
                                style={{color: theme.color.gray6}}
                                className="mb-2 d-block"
                            >
                                {t('settings:partner', 'Partner')}
                            </p>
                            <p data-testid="settings-partnerValue">
                                {formState.partnerName || t('settings:noPartner', 'No partner organization')}
                            </p>
                        </div>
                        {formState.partnerName && hasPermission(TenantPermission.RemovePartner) && (
                            <>
                                <RemovePartner
                                    onRemovePartner={handleRemovePartner}
                                    partnerName={formState.partnerName}
                                />
                            </>
                        )}
                        {!formState.partnerName && hasPermission(TenantPermission.AddPartner) && (
                            <AddPartner onAddPartner={handleAddPartner} />
                        )}
                    </div>
                </fieldset>

                <fieldset className="mb-6">
                    <legend className="subtitle-large">{t('settings:security', 'Security')}</legend>

                    <div className="my-3">
                        <Checkbox
                            data-testid="2fa-checkbox"
                            label={t('settings:require2FA', 'Require two-factor authentication')}
                            checked={formState.require2fa}
                            onChange={handleTwoFactorChange}
                        />
                        <p className="caption">
                            {t(
                                'settings:require2FADescription',
                                'Require all users with access to the HP Wolf Security Portal to enable two-factor authentication before they can log in.'
                            )}
                        </p>
                    </div>
                </fieldset>

                <fieldset className="mb-6">
                    <legend className="subtitle-large">
                        {t('settings:azureSso', 'Microsoft Entra ID single sign-on')}
                    </legend>

                    <div className="my-3">
                        <div className="my-3 d-flex align-items-end">
                            <TextBox
                                value={formState.azureTenantId ?? ''}
                                id="azure_tenant_id"
                                label={t('settings:azureTenantId', 'Microsoft Entra tenant ID')}
                                onChange={handleAzureTenantIdChange}
                                error={formError.azureTenantId?.[0] !== undefined}
                                helperText={formError.azureTenantId?.[0]}
                                helperTextState="error"
                                separateLabel
                            />
                            <ButtonLink
                                href="https://kb.bromium.com/article/Azure-SSO-Configuration/"
                                target="_blank"
                                rel="noreferrer"
                                appearance="secondary"
                                outerClassName="ms-5"
                                leadingIcon={<IconHelp />}
                            />
                        </div>
                        <Trans
                            i18nKey="settings:azureTenantIdDescription"
                            shouldUnescape
                        >
                            <p className="caption">
                                Enter your Microsoft Entra tenant ID to enable login using Entra ID. For more details
                                please refer to{' '}
                                <A
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://docs.microsoft.com/en-us/azure/active-directory/fundamentals/active-directory-how-to-find-tenant"
                                >
                                    this Microsoft article
                                </A>
                                .
                            </p>
                        </Trans>
                    </div>
                </fieldset>

                <div className="d-flex justify-content-end mt-6">
                    <ButtonLink
                        href={cancelUrl}
                        small
                        appearance="secondary"
                        data-testid="settings-cancel-button"
                    >
                        {t('settings:cancel', 'Cancel')}
                    </ButtonLink>
                    <Button
                        small
                        className="ms-3"
                        loading={isSaving}
                        type="submit"
                    >
                        {t('settings:save', 'Save')}
                    </Button>
                </div>
            </form>
        </>
    );
};

export default Settings;
